import {
  format,
  addDays,
  isSameDay,
  isToday,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isAfter,
  isBefore,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";

interface DateSelectorProps {
  selectedDates: Date[];
  onDateToggle: (date: Date) => void;
}

const DateSelector = ({ selectedDates, onDateToggle }: DateSelectorProps) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // ✅ Allow selection only within next 3 weeks
  const maxSelectableDate = addDays(today, 21);

  const [currentMonth, setCurrentMonth] = useState(today);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd,
  });

  const isDateSelected = (date: Date) =>
    selectedDates.some((d) => isSameDay(d, date));

  // ✅ Updated disable logic
  const isDateDisabled = (date: Date) => {
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);

    return (
      isBefore(dateOnly, today) || // before today
      isAfter(dateOnly, maxSelectableDate) // after 3 weeks
    );
  };

  const isOutsideMonth = (date: Date) => {
    return date.getMonth() !== currentMonth.getMonth();
  };

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => addDays(startOfMonth(prev), -1));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => addDays(endOfMonth(prev), 1));
  };

  return (
    <div className="space-y-3">
      {/* Month/Year header */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevMonth}
          className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <span className="text-sm font-semibold text-foreground">
          {format(currentMonth, "MMMM yyyy")}
        </span>

        <button
          onClick={handleNextMonth}
          className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
          aria-label="Next month"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-muted-foreground py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((date) => {
          const selected = isDateSelected(date);
          const disabled = isDateDisabled(date);
          const todayDate = isToday(date);
          const outside = isOutsideMonth(date);

          return (
            <button
              key={date.toISOString()}
              onClick={() => !disabled && !outside && onDateToggle(date)}
              disabled={disabled || outside}
              className={cn(
                "relative w-9 h-9 mx-auto rounded-full flex items-center justify-center text-xs font-medium transition-all duration-200",
                outside && "text-muted-foreground/30 cursor-default",
                disabled &&
                  !outside &&
                  "text-muted-foreground/40 cursor-not-allowed",
                !disabled &&
                  !outside &&
                  !selected &&
                  "text-foreground hover:bg-secondary",
                selected && "bg-accent text-accent-foreground",
                todayDate &&
                  !selected &&
                  !outside &&
                  "ring-1 ring-accent text-accent",
              )}
              aria-label={format(date, "EEEE, MMMM d")}
              aria-pressed={selected}
            >
              {format(date, "d")}
            </button>
          );
        })}
      </div>

      {selectedDates.length > 0 && (
        <div className="text-xs text-muted-foreground text-center pt-1">
          {selectedDates.length} date
          {selectedDates.length > 1 ? "s" : ""} selected
        </div>
      )}
    </div>
  );
};

export default DateSelector;
